import { useState } from 'react';
import { useAppStore, type Bot, type Listing } from '../state/store';
import { EbayStorePanel, EtsyStorePanel } from './StorePanels';

// ---- helpers ---------------------------------------------------------------

const STATUS_LABEL: Record<Bot['status'], string> = {
  idle: 'STBY', working: 'EXEC', awaiting_approval: 'APPR_REQ', alert: 'ERR', paused: 'PAUSED',
};
const STATUS_COLOR: Record<Bot['status'], string> = {
  idle: 'text-term-green/40', working: 'text-term-green animate-pulse', awaiting_approval: 'text-yellow-400',
  alert: 'text-red-500 animate-pulse', paused: 'text-term-green/20',
};

const LISTING_STATUS_COLOR: Record<string, string> = {
  draft: 'text-term-green/40', pending_approval: 'text-yellow-400', approved: 'text-term-green',
  active: 'text-term-green', sold: 'text-[#ff00ff]', ended: 'text-term-green/20',
  error: 'text-red-500', rejected: 'text-red-500',
};

const SENTIMENT_COLOR: Record<string, string> = {
  positive: 'text-term-green', neutral: 'text-term-green/40', negative: 'text-red-500',
};

function cents(n: number) { return `$${(n / 100).toFixed(2)}`; }

// ---- sub-panels ------------------------------------------------------------

function CrewPanel() {
  const { bots, approvals, decideApproval, triggerAction, actionBusy } = useAppStore();
  const ACTIONS: Array<{ id: 'scout' | 'navigator' | 'quartermaster' | 'comms'; label: string }> = [
    { id: 'scout', label: './scout.sh' },
    { id: 'navigator', label: './promote.sh' },
    { id: 'quartermaster', label: './enrich.sh' },
    { id: 'comms', label: './triage.sh' },
  ];
  return (
    <div className="space-y-4 font-mono">
      <div className="term-card p-3">
        <div className="term-corners" />
        <p className="term-label mb-2">SYSTEM.PROCESSES</p>
        <ul className="space-y-2">
          {bots.map((bot) => (
            <li key={bot.id} className="flex justify-between items-center border-b border-term-green/10 pb-1">
              <span className="text-term-green/80 uppercase tracking-widest text-[10px]">{bot.name}</span>
              <span className={`text-[10px] tracking-widest ${STATUS_COLOR[bot.status]}`}>[{STATUS_LABEL[bot.status]}]</span>
            </li>
          ))}
        </ul>
        <div className="grid grid-cols-2 gap-2 pt-3">
          {ACTIONS.map(({ id, label }) => (
            <button
              key={id}
              disabled={!!actionBusy}
              onClick={() => triggerAction(id)}
              className="term-btn text-left"
            >
              <span className="text-term-green/40 mr-1">&gt;</span>
              {actionBusy === id ? 'EXECUTING...' : label}
            </button>
          ))}
        </div>
      </div>

      {approvals.length > 0 && (
        <div className="term-card p-3 border-yellow-500/50" style={{ boxShadow: '0 0 10px rgba(234,179,8,0.1)' }}>
          <div className="term-corners before:border-yellow-500/50 after:border-yellow-500/50" />
          <p className="text-[10px] uppercase tracking-widest text-yellow-500 mb-2 animate-pulse">
            ! APPR_REQ ({approvals.length})
          </p>
          <ul className="space-y-2">
            {approvals.map((a) => {
              const p = a.payload as Record<string, unknown>;
              const title = (p.title ?? p.subject ?? p.incoming ?? `#${a.id}`) as string;
              return (
                <li key={a.id} className="border border-yellow-500/20 bg-yellow-500/5 p-2">
                  <div className="flex items-center justify-between mb-1 opacity-70">
                    <span className="text-[9px] uppercase tracking-widest text-yellow-500">[{a.botId}_{a.kind}]</span>
                    <span className="text-[9px] text-yellow-500/50">ID:{a.id}</span>
                  </div>
                  <p className="text-[11px] text-yellow-500/90 truncate">{String(title).slice(0, 60)}</p>
                  {a.kind === 'listing' && (
                    <p className="text-[10px] text-yellow-500/50 mt-1">
                      VAL:{cents(p.priceCents as number)} PRF:{cents(p.estProfitCents as number)}
                    </p>
                  )}
                  <div className="mt-2 flex gap-2">
                    <button
                      onClick={() => decideApproval(a.id, 'approve')}
                      className="term-btn flex-1 border-yellow-500/50 text-yellow-500 hover:bg-yellow-500/10 hover:border-yellow-500 hover:shadow-[0_0_8px_rgba(234,179,8,0.4)]"
                    >Y/CONFIRM</button>
                    <button
                      onClick={() => decideApproval(a.id, 'reject')}
                      className="term-btn flex-1 border-red-500/50 text-red-500 hover:bg-red-500/10 hover:border-red-500 hover:shadow-[0_0_8px_rgba(239,68,68,0.4)]"
                    >N/ABORT</button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

// ---- Modals & Subcomponents removed for brevity (they still exist but simplified for space) ----

function ListingsPanel() {
  const { listings } = useAppStore();
  const grouped: Record<string, Listing[]> = {};
  for (const l of listings) {
    (grouped[l.status] ??= []).push(l);
  }
  const ORDER = ['pending_approval', 'approved', 'active', 'draft', 'sold', 'ended', 'error', 'rejected'];
  const sorted = ORDER.flatMap((s) => grouped[s] ?? []);

  return (
    <div className="space-y-2 font-mono">
      {sorted.length === 0 && (
        <p className="text-term-green/40 text-[10px] uppercase tracking-widest">0 RECORDS FOUND</p>
      )}
      {sorted.map((l) => (
        <div key={l.id} className="term-card p-2 hover:border-term-green hover:shadow-[0_0_8px_rgba(0,255,65,0.2)] transition-all cursor-pointer">
          <div className="flex items-center justify-between mb-1">
            <span className={`text-[9px] uppercase tracking-widest border border-current px-1 ${LISTING_STATUS_COLOR[l.status] ?? 'text-term-green/50'}`}>
              {l.status}
            </span>
            <span className="text-[9px] text-term-green/40">SCR:{l.navigatorScore}</span>
          </div>
          <p className="text-[10px] text-term-green/90 truncate">{l.title}</p>
          <p className="mt-1 text-[9px] text-term-green/50">
            {cents(l.priceCents)} · PRF:{cents(l.estProfitCents)} · {(l.profitMargin * 100).toFixed(0)}%
          </p>
        </div>
      ))}
    </div>
  );
}

function MessagesPanel() {
  const { messages } = useAppStore();
  return (
    <div className="space-y-2 font-mono">
      {messages.length === 0 && <p className="text-term-green/40 text-[10px] uppercase tracking-widest">INBOX EMPTY</p>}
      {messages.map((m) => (
        <div key={m.id} className="term-card p-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[9px] uppercase tracking-widest text-term-green/60 border border-term-green/30 px-1">{m.replyStatus}</span>
            <span className={`text-[9px] uppercase tracking-widest ${SENTIMENT_COLOR[m.sentiment ?? ''] ?? 'text-term-green/40'}`}>
              {m.intent ?? 'UNK'}
            </span>
          </div>
          <p className="text-[10px] text-term-green/90 truncate">&gt; {m.buyer}: {m.subject ?? m.body.slice(0, 40)}</p>
        </div>
      ))}
    </div>
  );
}

// ---- main panel ------------------------------------------------------------

type Tab = 'crew' | 'listings' | 'messages' | 'storefront' | 'ebay' | 'etsy';

export function StatusPanel() {
  const [tab, setTab] = useState<Tab>('crew');

  const TABS: Array<{ id: Tab; label: string; }> = [
    { id: 'crew', label: 'SYS.OPS' },
    { id: 'ebay', label: 'NET.EBAY' },
    { id: 'etsy', label: 'NET.ETSY' },
    { id: 'listings', label: 'DAT.LST' },
    { id: 'messages', label: 'DAT.MSG' },
  ];

  return (
    <aside className="w-80 shrink-0 flex flex-col border-l border-term-border bg-term-bg relative">
      <div className="term-scan-bar" />
      {/* Tab bar */}
      <div className="flex flex-wrap border-b border-term-border p-1 gap-1">
        {TABS.map(({ id, label }) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`term-btn flex-1 min-w-[30%] ${tab === id ? 'bg-term-green/10 border-term-green text-term-green shadow-[0_0_8px_rgba(0,255,65,0.4)]' : ''}`}
          >
            {label}
          </button>
        ))}
      </div>
      {/* Content */}
      <div className="flex-1 overflow-y-auto p-3 term-cursor">
        {tab === 'crew' && <CrewPanel />}
        {tab === 'listings' && <ListingsPanel />}
        {tab === 'messages' && <MessagesPanel />}
        {tab === 'ebay' && <EbayStorePanel />}
        {tab === 'etsy' && <EtsyStorePanel />}
      </div>
    </aside>
  );
}
